import React, { useEffect, useState } from "react";
import BackButton from "../components/backButton";
import { TopAdminNav } from "../layouts/TopAdminNav";
import { RegisterCard } from "../components/RegisterCard";
import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import {
  useDeleteSubjectMutation,
  useGetClassroomsQuery,
  useGetSubjectForClassesQuery,
  useUnassignSubjectToClassMutation,
} from "../hooks/useAdmin";
import loader from "../assets/loader.gif";
import moment from "moment";
import Swal from "sweetalert2";
import CustomModal from "../components/CustomModal/CustomModal";
import { SubjectForm } from "../layouts/editForms/SubjectForm";
import SmallModal from "../components/SmallModal";
import { CircleLoader } from "../components/CircleLoader";

export interface ISubject {
  id: string;
  school_id: string;
  name: string;
  code: string;
  description: string;
  created_at: string;
  updated_at: string;
}

const RegisterClassSubject = () => {
  const { data: classSubjects, isLoading } = useGetSubjectForClassesQuery();
  const deleteSub = useDeleteSubjectMutation();
  const [openModal, setOpenModal] = useState(false);
  const [classRoomId, setClassRoomId] = useState("");
  const [singleSubject, setSingleSubject] = useState<ISubject | null>(null);
  const [openUnassignModal, setOpenUnassignModal] = useState(false);
  const { data: classrooms } = useGetClassroomsQuery();
  const unassign = useUnassignSubjectToClassMutation({
    close: () => setOpenUnassignModal(false),
  });

  async function deleteSubject(subjectId: string) {
    Swal.fire({
      icon: "warning",
      title: "Delete Subject",
      text: "Are you sure you want to delete this subject",
      showCancelButton: true,
      confirmButtonColor: "#f43f5e",
      confirmButtonText: "Yes, Delete",
      cancelButtonColor: "#64748b",
      cancelButtonText: "No, Cancel",
    }).then((response) => {
      if (response.isConfirmed) {
        deleteSub.mutate({
          subjectId,
        });
      }
    });
  }

  useEffect(() => {
    setClassRoomId(classrooms?.data[0]?.id || "");
  }, [classrooms]);

  const filteredSubjects = classSubjects?.data?.filter(
    (c: { id: string }) => c.id === classRoomId,
  );

  const handleUnassign = () => {
    unassign.mutate({
      classroomId: classRoomId ?? "",
      subject_ids: [singleSubject!.id],
    });
  };

  const filteredClassName = classrooms?.data?.filter(
    (c: { id: string }) => c?.id === classRoomId,
  )[0]?.name;

  return (
    <div className="w-full">
      <div className="w-full flex items-center mb-4">
        <BackButton />
        <TopAdminNav name="Jane Doe" title="Class Curriculum" />
      </div>

      <div className="flex w-full">
        <div className="block w-full h-[87vh] overflow-auto register-scroll">
          <div className="flex px-1 mb-5">
            <div className="w-full">
              <RegisterCard
                title="Class Curriculum Configuration"
                details="Monitor and map allocated subject tracks assigned across specific classrooms"
              />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 max-w-[98%] mx-auto">
            {/* Top Operational Action Bar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  Currently Viewing
                </span>
                <h3 className="text-base font-bold text-slate-800 tracking-tight">
                  Curriculum for{" "}
                  <span className="text-[#0096D1]">
                    {filteredSubjects && filteredSubjects[0]
                      ? filteredSubjects[0]?.name
                      : filteredClassName || "All Classes"}
                  </span>
                </h3>
              </div>

              {classrooms?.data?.length && (
                <div className="w-full sm:w-auto relative">
                  <select
                    onChange={(e) => setClassRoomId(e.target.value)}
                    value={classRoomId}
                    className="w-full sm:w-[240px] rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-700 bg-slate-50/50 focus:outline-none focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748b%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:1.1rem_1.1rem] bg-[right_0.875rem_center] bg-no-repeat pr-10"
                  >
                    <option value="">All Classrooms</option>
                    {classrooms?.data?.map((clas: any, idx: number) => (
                      <option key={idx} value={clas.id}>
                        {clas.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Structured Main Data Table View */}
            <div className="w-full overflow-x-auto rounded-xl border border-slate-50">
              <table className="w-full border-collapse text-left table-fixed min-w-[700px]">
                <thead>
                  <tr className="bg-[#F4F7FE] border-b border-slate-100 text-[11px] font-bold tracking-wider text-[#012063] uppercase">
                    <th className="py-3.5 px-4 w-[40%] text-left">
                      Subject Identifier Name
                    </th>
                    <th className="py-3.5 px-4 w-[25%] text-center">
                      System Code
                    </th>
                    <th className="py-3.5 px-4 w-[20%] text-center">
                      Allocation Date
                    </th>
                    <th className="py-3.5 px-4 w-[15%] text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {isLoading ? (
                    <tr>
                      <td colSpan={4} className="py-12">
                        <div className="flex justify-center items-center">
                          <img
                            src={loader}
                            alt="loader"
                            className="w-10 h-10 object-contain"
                          />
                        </div>
                      </td>
                    </tr>
                  ) : classSubjects &&
                    filteredSubjects?.[0]?.subjects?.length > 0 ? (
                    filteredSubjects[0].subjects.map(
                      (item: any, index: number) => (
                        <tr
                          key={index}
                          className="group bg-white hover:bg-slate-50/40 transition-colors duration-150 text-[13px] text-slate-600 font-medium"
                        >
                          <td className="py-3.5 px-4">
                            <span className="font-bold text-slate-800 tracking-tight capitalize group-hover:text-[#0096D1] transition-colors">
                              {item.name ?? "—"}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 text-center whitespace-nowrap">
                            <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono text-xs uppercase font-bold tracking-wider border border-slate-200/40">
                              {item.code ? item.code.slice(0, 12) : "—"}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 text-center text-xs text-slate-400 font-medium whitespace-nowrap">
                            {moment(item?.created_at).format("MM/DD/YY")}
                          </td>
                          <td className="py-3.5 px-4 text-center whitespace-nowrap align-middle">
                            <Menu
                              menuButton={
                                <MenuButton className="inline-flex items-center justify-center gap-1 rounded-xl px-3 py-1.5 bg-slate-50 border border-slate-200 text-slate-700 text-xs font-bold shadow-sm hover:bg-slate-100 active:scale-95 transition-all">
                                  Options
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={2.5}
                                    stroke="currentColor"
                                    className="w-2.5 h-2.5 text-slate-400"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                                    />
                                  </svg>
                                </MenuButton>
                              }
                              transition
                              align="end"
                              offsetY={4}
                              menuClassName="bg-white border border-slate-100 rounded-xl shadow-xl p-1.5 min-w-[160px]"
                            >
                              <MenuItem
                                onClick={() => {
                                  setSingleSubject(item);
                                  setOpenModal(true);
                                }}
                                className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-600 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                              >
                                Edit Subject
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  setSingleSubject(item);
                                  setOpenUnassignModal(true);
                                }}
                                className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-orange-600 rounded-lg cursor-pointer hover:bg-orange-50 transition-colors"
                              >
                                Unassign Subject
                              </MenuItem>
                              <div className="my-1 border-t border-slate-50" />
                              <MenuItem
                                onClick={() => deleteSubject(item?.id)}
                                className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-rose-600 rounded-lg cursor-pointer hover:bg-rose-50 transition-colors"
                              >
                                Delete Subject
                              </MenuItem>
                            </Menu>
                          </td>
                        </tr>
                      ),
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className="py-8 text-center text-slate-400 text-sm"
                      >
                        No configured tracking metrics discovered for this
                        classroom...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Primary Modal Modification Configuration View */}
      {openModal && (
        <CustomModal
          title={
            singleSubject
              ? "Edit Subject Configuration"
              : "Create Curriculum Subject"
          }
          width={60}
          hideModal={() => setOpenModal(false)}
        >
          <SubjectForm
            singleSubject={singleSubject}
            close={() => setOpenModal(false)}
          />
        </CustomModal>
      )}

      {/* Unassign Confirmation Small Overlay Modal Container */}
      {openUnassignModal && (
        <SmallModal
          title="Revoke Subject Allocation"
          isOpen={openUnassignModal}
          onClose={() => setOpenUnassignModal(false)}
        >
          <div className="flex flex-col items-center gap-4 text-center p-2">
            <div className="h-12 w-12 rounded-full bg-orange-50 text-orange-500 flex items-center justify-center text-xl mb-1">
              ⚠️
            </div>
            <h3 className="text-sm font-medium text-slate-700 max-w-xs leading-relaxed">
              Are you sure you want to unassign{" "}
              <span className="font-bold text-slate-900">
                {singleSubject?.name}
              </span>{" "}
              from{" "}
              <span className="font-bold text-slate-900">
                {filteredClassName}
              </span>
              ?
            </h3>

            <div className="w-full flex flex-col gap-2 pt-2">
              <button
                onClick={handleUnassign}
                disabled={unassign?.isPending}
                type="button"
                className="w-full inline-flex items-center justify-center rounded-xl px-5 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-400 text-white font-bold text-sm shadow-md transition-all transform active:scale-95 gap-2"
              >
                {unassign?.isPending ? (
                  <>
                    <CircleLoader />
                    <span>Processing Change...</span>
                  </>
                ) : (
                  "Confirm Unassignment"
                )}
              </button>

              <button
                type="button"
                onClick={() => setOpenUnassignModal(false)}
                className="w-full py-2 text-xs font-bold text-slate-500 hover:text-slate-700 transition-colors"
              >
                Cancel Action
              </button>
            </div>
          </div>
        </SmallModal>
      )}
    </div>
  );
};

export default RegisterClassSubject;
